import React, { useState } from 'react';
import { X, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { stcBinCategoryService, CreateStcBinCategoryRequest } from '../../../../../services/stcBinCategoryService';
import { useAuth } from '../../../../../contexts/AuthContext';

interface BinCategoryCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const BinCategoryCreateModal: React.FC<BinCategoryCreateModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const { user } = useAuth();

    const [formData, setFormData] = useState<CreateStcBinCategoryRequest>({
        companyNo: user?.companyNo || '001',
        stcBinCatNo: 1,
        lng_1: '',
        lng_2: '',
        lng_3: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.lng_1.trim()) {
            setError('Beskrivelse er obligatorisk');
            return;
        }
        if (!formData.stcBinCatNo || formData.stcBinCatNo <= 0) {
            setError('Kategori nr. er obligatorisk og må være større enn 0');
            return;
        }

        try {
            setIsSaving(true);
            setError('');
            await stcBinCategoryService.create(formData);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSuccess();
            }, 1200);
        } catch (err: any) {
            setError(err.message || 'Feil ved opprettelse av lokasjonskategori');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">Ny lokasjonskategori</h2>
                            <p className="text-sm text-green-100">Opprett en ny lokasjonskategori</p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">Lokasjonskategori opprettet!</p>
                                </div>
                            </div>
                        )}

                        <div>
                            <label htmlFor="stcBinCatNo" className="block text-sm font-medium text-gray-700 mb-1">
                                Kategori nr. <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="stcBinCatNo"
                                type="number"
                                value={formData.stcBinCatNo}
                                onChange={(e) => setFormData(prev => ({ ...prev, stcBinCatNo: parseInt(e.target.value) || 0 }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                required
                                autoFocus
                                min={1}
                            />
                        </div>

                        <div>
                            <label htmlFor="lng_1" className="block text-sm font-medium text-gray-700 mb-1">
                                Beskrivelse <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="lng_1"
                                type="text"
                                value={formData.lng_1}
                                onChange={(e) => setFormData(prev => ({ ...prev, lng_1: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Beskrivelse (obligatorisk)"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="lng_2" className="block text-sm font-medium text-gray-700 mb-1">
                                Språk 2
                            </label>
                            <input
                                id="lng_2"
                                type="text"
                                value={formData.lng_2 || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, lng_2: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Valgfritt"
                            />
                        </div>

                        <div>
                            <label htmlFor="lng_3" className="block text-sm font-medium text-gray-700 mb-1">
                                Språk 3
                            </label>
                            <input
                                id="lng_3"
                                type="text"
                                value={formData.lng_3 || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, lng_3: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Valgfritt"
                            />
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Avbryt
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Oppretter...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        Opprett lokasjonskategori
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
